import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Segment,
    SegmentContent,
    SegmentHeader,
} from '@/platform/ui';
import { EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import CommunicationSection from './CommunicationSection';

const metaLine = questionnaire =>
    [
        `Aangemaakt: ${localDate(questionnaire.created_at)}`,
        questionnaire.sent_at &&
            `Verstuurd: ${localDate(questionnaire.sent_at)}`,
        questionnaire.first_viewed_at &&
            `Eerst bekeken: ${localDate(questionnaire.first_viewed_at)}`,
        questionnaire.completed_at &&
            `Afgerond: ${localDate(questionnaire.completed_at)}${questionnaire.completed_by ? ` door ${questionnaire.completed_by}` : ''}`,
    ]
        .filter(Boolean)
        .join(' · ');

// Building, ready and in progress: not completed yet, so nothing has acted on the answers.
const OPEN_STATES = [1, 2, 4];
// Ready and in progress: Ops can fill these in on behalf of the employee.
const EDITABLE_STATES = [2, 4];

const QuestionnaireCard = ({ questionnaire }) => {
    const { auth, employee } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();
    const canUpdate = auth?.permissions?.includes('update questionnaires');

    const handleDelete = () =>
        confirm({
            title: 'Vragenlijst verwijderen?',
            description:
                'Weet je zeker dat je deze vragenlijst wilt verwijderen? De bijbehorende communicatie wordt ook verwijderd.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/questionnaires/${questionnaire.id}`,
                    {
                        preserveScroll: true,
                        onFinish: close,
                    }
                );
            },
        });

    return (
        <Segment>
            <SegmentHeader
                header={
                    <span className='inline-flex items-center gap-2'>
                        Vragenlijst
                        <Badge color={questionnaire.state_color}>
                            {questionnaire.state_label}
                        </Badge>
                    </span>
                }
                subheader={metaLine(questionnaire)}
            >
                {questionnaire.requires_consent && (
                    <Badge
                        color={questionnaire.has_consent ? 'green' : 'orange'}
                    >
                        {questionnaire.has_consent
                            ? 'Toestemming gegeven'
                            : 'Toestemming vereist'}
                    </Badge>
                )}
                {canUpdate && EDITABLE_STATES.includes(questionnaire.state) && (
                    <Button
                        as={Link}
                        href={`/customers/${employee.customer_id}/employees/${employee.id}/questionnaires/${questionnaire.id}/edit`}
                        aria-label='Vragenlijst invullen'
                    >
                        <PencilIcon className='size-4' />
                    </Button>
                )}
                <Button
                    as={Link}
                    href={`/customers/${employee.customer_id}/employees/${employee.id}/questionnaires/${questionnaire.id}`}
                    aria-label='Vragenlijst bekijken'
                >
                    <EyeIcon className='size-4' />
                </Button>
                {canUpdate && OPEN_STATES.includes(questionnaire.state) && (
                    <Button
                        type='button'
                        variant='danger'
                        aria-label='Vragenlijst verwijderen'
                        onClick={handleDelete}
                    >
                        <TrashIcon className='size-4' />
                    </Button>
                )}
            </SegmentHeader>
            <SegmentContent>
                <CommunicationSection
                    communication={questionnaire.communication}
                    emptyText='Er is geen communicatie voor deze vragenlijst.'
                />
            </SegmentContent>
        </Segment>
    );
};

export default QuestionnaireCard;
